// One post, rendered the way an employee sees it rather than as a table row.
//
// ── WHY LIKE/COMMENT CAN FAIL ON A CARD YOU ARE LOOKING AT ──────────────────
//
// The list behind this view comes from the MODERATION endpoint, which sees every post
// in the project. Liking and commenting go to the EMPLOYEE feed endpoints, which
// enforce the ordinary audience rules — so a narrowed post the console can display is
// one the console may not be allowed to react to, and the server answers 404 (never
// 403, deliberately, so a post id can't be used to probe what exists elsewhere).
//
// That mismatch is real and cannot be papered over: showing the buttons only on posts
// we predict will work would need us to re-derive the audience client-side, which is
// exactly the duplication the server-authoritative design avoids. So the controls are
// always offered and a refusal is explained in place.
//
// The one exception is a post that is not PUBLISHED under the Connect content policy
// (held for review, or not approved). There the answer is certain rather than
// predicted — the feed routes 404 every like and comment on it, for everyone — so the
// controls are disabled up front instead of failing with the misleading "only posts
// that were shared with you".
//
// ── ICONS ACT, COUNTS DRILL DOWN ────────────────────────────────────────────
//
// The heart and the speech bubble do what they always did — like, open the thread.
// The NUMBER beside each, and the eye with its view count, open the engagement panel
// on who liked / commented / has seen the post. Those stay clickable on a removed or
// held post (and at 0): the moderation endpoints behind them see every post state, and
// "who saw this before it came down" is asked most about exactly those posts.

import { useState } from 'react';
import {
  Heart, MessageCircle, Eye, Pin, ShieldAlert, Trash2, RotateCcw, Pencil, X,
} from 'lucide-react';
import AuthedImage from '../AuthedImage';
import { InlineLoader } from '../loading';
import { socialFeedAPI } from '../../services/api';
import type { EngagementTab } from '../../services/engagementApi';
import { fmtCount } from './EngagementPanel';

export interface FeedAuthor {
  empCode: string;
  name: string;
  designation: string | null;
  storeName: string | null;
  photo: string | null;
}

export interface FeedMedia {
  id: string;
  path: string;
  thumbnailPath: string | null;
}

/**
 * ⚠️ EVERY FIELD THE FEED VIEW ADDED IS OPTIONAL, and the optionality is load-bearing.
 *
 * This card renders rows from the MODERATION list endpoint, which the table view has
 * always consumed. The feed view needs more from that row — full text, media, audience
 * — and those arrive only from an API build that includes them. A web deploy that
 * lands ahead of the API therefore serves rows with those fields ABSENT.
 *
 * They were typed as required, so nothing forced a guard, and `post.media.length` threw
 * `Cannot read properties of undefined` — the whole All-posts tab replaced by an error
 * boundary, table view included. Optional here means TypeScript makes the next person
 * handle the missing case instead of trusting the deploy order.
 */
export interface FeedPost {
  postId: string;
  author: FeedAuthor;
  content?: string | null;
  /** The TRUNCATED preview the table column has always used. Rendered when `content`
   *  is absent, i.e. when the API predates the feed view — so the card degrades to a
   *  clipped post rather than a card with no words in it. */
  contentPreview?: string | null;
  postType?: 'post' | 'announcement';
  isPinned?: boolean;
  audience?: string;
  audienceTarget?: string | null;
  editedAt?: string | null;
  media?: FeedMedia[] | null;
  /** Who this post @-tagged. Absent on an older API; the renderer degrades to plain
   *  text, so the field is optional rather than defaulted. */
  mentions?: Array<{ empCode: string; name: string }> | null;
  likeCount: number;
  /** Whether the console user has already liked this. Seeded from the list response —
   *  without it every heart renders empty and the first click on a post you already
   *  liked is a server no-op the optimistic count has already incremented. */
  likedByMe?: boolean;
  commentCount: number;
  /** People who have seen it, each counted once and the author never
   *  (social_posts.view_count). Absent on an API build that predates it, where the
   *  eye shows a dash rather than a false 0. */
  viewCount?: number;
  reportCount: number;
  status: string;
  createdAt: string;
  deletedAt: string | null;
  deletedReason: string | null;
  /** Connect content policy state. Absent on an older API, where every post is
   *  published — hence optional, and read through `status` as a fallback. */
  reviewState?: 'published' | 'held' | 'rejected';
  reviewNote?: string | null;
}

interface Comment {
  id: string;
  author: FeedAuthor;
  content: string;
  createdAt: string;
  /** 'held' = only its author (here, the console user) can see it until approved. */
  reviewState?: 'published' | 'held' | 'rejected';
}

/**
 * Human label for a stored audience.
 *
 * A TARGETED post carries audience='team' plus the real target, because mobile's card
 * falls through to "Everyone" on any audience string it doesn't know and would
 * mislabel a restricted post as project-wide. The console has no such constraint, so
 * it reads the target and says exactly who the post was for.
 */
function audienceLabel(p: FeedPost): string {
  if (p.audienceTarget) {
    const t = p.audienceTarget;
    if (t === 'level:th') return 'All Trade Heads';
    if (t === 'level:am') return 'All Assistant Managers';
    if (t === 'level:th_am') return 'All Trade Heads & AMs';
    if (t.startsWith('mgr:')) return `${t.slice(4)}’s team`;
    return t;
  }
  if (p.audience === 'team') return 'Their team';
  if (p.audience === 'peers') return 'Their peers';
  return 'Everyone';
}

/**
 * Post text with its @-tags tinted.
 *
 * THE TEXT DECIDES WHAT IS SHOWN; `mentions` only says which "@Name" runs are real.
 * That split is why the server stores a readable name in the body rather than a token —
 * anything that cannot parse this still renders a correct, if plain, sentence.
 *
 * Returns the bare string when there is nothing to mark, so the common post costs no
 * extra nodes. Longest name first: "Anita" and "Anita Rao" can both be tagged in one
 * post, and matching the short one first would tint half the long one.
 */
function renderTaggedText(
  body: string,
  mentions: Array<{ empCode: string; name: string }> | null | undefined,
): React.ReactNode {
  const names = (mentions ?? []).map((m) => m.name).filter(Boolean).sort((a, b) => b.length - a.length);
  if (names.length === 0) return body;

  const out: React.ReactNode[] = [];
  let rest = body;
  let key = 0;
  // Always take the EARLIEST match so output stays in reading order — scanning
  // name-by-name would emit the tags grouped by person instead.
  while (rest.length > 0) {
    let at = -1;
    let hit = '';
    for (const n of names) {
      const i = rest.indexOf(`@${n}`);
      if (i !== -1 && (at === -1 || i < at)) { at = i; hit = n; }
    }
    if (at === -1) { out.push(rest); break; }
    if (at > 0) out.push(rest.slice(0, at));
    out.push(
      <span key={`m${key++}`} className="font-semibold text-blue-700">{`@${hit}`}</span>
    );
    rest = rest.slice(at + hit.length + 1);
  }
  return out;
}

const timeAgo = (iso: string): string => {
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';

interface Props {
  post: FeedPost;
  projectId: number;
  onReview: (post: FeedPost) => void;
  /** Opens the engagement panel on `tab`. Without it the counts render as plain text. */
  onEngagement?: (post: FeedPost, tab: EngagementTab) => void;
}

export default function FeedPostCard({ post, projectId, onReview, onEngagement }: Props) {
  // Normalised ONCE, here, so no JSX below has to remember the field might be absent —
  // that assumption is what crashed the tab. An older API simply yields no photos.
  const media = post.media ?? [];
  // Full text when the API sends it, the table's truncated preview otherwise.
  const bodyText = post.content ?? post.contentPreview ?? '';

  const [liked, setLiked] = useState(!!post.likedByMe);
  const [likes, setLikes] = useState(post.likeCount ?? 0);
  const [busy, setBusy] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [commentCount, setCommentCount] = useState(post.commentCount ?? 0);
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  /** The photo being viewed full size, or null. Holds the media row rather than a
   *  boolean so the overlay can request the FULL blob, never the thumbnail. */
  const [lightbox, setLightbox] = useState<FeedMedia | null>(null);

  const removed = !!post.deletedAt;
  // `status` carries the same fact from the moderation list ('held' / 'not_approved'),
  // so a row from an API that sends one but not the other still gets its badge.
  const reviewState: 'published' | 'held' | 'rejected' =
    post.reviewState ?? (post.status === 'held' ? 'held' : post.status === 'not_approved' ? 'rejected' : 'published');
  const unpublished = !removed && reviewState !== 'published';

  /** The number beside an icon: a drill-down when the page wired one, text otherwise. */
  const countButton = (tab: EngagementTab, n: number | null | undefined, hint: string, cls: string) =>
    onEngagement ? (
      <button
        type="button"
        onClick={() => onEngagement(post, tab)}
        title={hint}
        aria-label={`${fmtCount(n)} — ${hint.toLowerCase()}`}
        className={`rounded-md py-1.5 pl-1 pr-2.5 text-sm font-medium tabular-nums hover:bg-gray-100 ${cls}`}
      >
        {fmtCount(n)}
      </button>
    ) : (
      <span className={`py-1.5 pl-1 pr-2.5 text-sm font-medium tabular-nums ${cls}`}>{fmtCount(n)}</span>
    );

  // A 404 from these routes means "this post isn't shared with you", not "missing".
  const explain = (err: any, verb: string) => {
    const s = err?.response?.status;
    if (s === 404) return `You can only ${verb} posts that were shared with you.`;
    if (s === 403) return `Your role doesn’t have Connect posting access in this project.`;
    return err?.response?.data?.message || `Could not ${verb} that post.`;
  };

  const toggleLike = async () => {
    if (busy || removed || unpublished) return;
    const next = !liked;
    setBusy(true);
    setNotice(null);
    // Optimistic, reverted on refusal — the round trip is long enough that a heart
    // which waits for it feels broken.
    setLiked(next); setLikes((n) => n + (next ? 1 : -1));
    try {
      await socialFeedAPI.setLike(post.postId, next, projectId);
    } catch (err) {
      setLiked(!next); setLikes((n) => n + (next ? -1 : 1));
      setNotice(explain(err, 'react to'));
    } finally { setBusy(false); }
  };

  const openComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (!next || comments) return;
    try {
      const res = await socialFeedAPI.listComments(post.postId, projectId);
      setComments(res.data?.data || []);
    } catch (err) {
      setComments([]);
      setNotice(explain(err, 'read comments on'));
    }
  };

  const submitComment = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true); setNotice(null);
    try {
      const res = await socialFeedAPI.addComment(post.postId, body, projectId);
      const created = res.data?.data;
      setComments((prev) => [...(prev || []), created].filter(Boolean) as Comment[]);
      setDraft('');
      // HELD by the content policy: saved, shown here to its author, but not counted —
      // the server doesn't count it either until a moderator approves it.
      if (res.data?.review?.state === 'held' || created?.reviewState === 'held') {
        setNotice(res.data?.review?.message || 'Your comment will be shared after a moderator reviews it.');
      } else {
        setCommentCount((n) => n + 1);
      }
    } catch (err) {
      // A policy BLOCK (422) and the blocked-attempts limit (429) arrive with a
      // self-contained message, which explain() passes through; the draft is kept.
      setNotice(explain(err, 'comment on'));
    } finally { setBusy(false); }
  };

  return (
    <article className={`overflow-hidden rounded-xl border bg-white shadow-sm ${
      post.isPinned ? 'border-amber-200' : 'border-gray-200'} ${removed ? 'opacity-75' : ''}`}>

      {post.isPinned && (
        <div className="flex items-center gap-1.5 bg-amber-50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
          <Pin size={12} /> Pinned announcement
        </div>
      )}

      <div className="flex items-start gap-3 px-4 pb-2 pt-4">
        <div className="grid h-10 w-10 flex-none place-items-center rounded-full bg-blue-50 text-sm font-semibold text-blue-700">
          {initials(post.author?.name || '?')}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-gray-900">{post.author?.name || post.author?.empCode || 'Unknown'}</div>
          <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500">
            {post.author?.designation && <span className="truncate">{post.author.designation}</span>}
            {post.author?.storeName && <><span>·</span><span className="truncate">{post.author.storeName}</span></>}
            <span>·</span><span>{timeAgo(post.createdAt)}</span>
            {post.editedAt && <><span>·</span><span className="inline-flex items-center gap-0.5"><Pencil size={10} />edited</span></>}
            <span>·</span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
              post.audienceTarget || post.audience !== 'project'
                ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
              {audienceLabel(post)}
            </span>
          </div>
        </div>
        <div className="flex flex-none items-center gap-1.5">
          {post.reportCount > 0 && (
            <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
              {post.reportCount} report{post.reportCount > 1 ? 's' : ''}
            </span>
          )}
          {removed && (
            <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">Removed</span>
          )}
          {!removed && reviewState === 'held' && (
            <span
              className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
              title="Only the author can see this until a moderator approves it in AI review"
            >
              Held for review
            </span>
          )}
          {!removed && reviewState === 'rejected' && (
            <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">Not approved</span>
          )}
        </div>
      </div>

      <div className="px-4 pb-3">
        {bodyText && (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
            {renderTaggedText(bodyText, post.mentions)}
          </p>
        )}
        {media.length > 0 && (
          // WIDTH-CAPPED, NOT FULL-BLEED. `w-full` with a 4:3 aspect looked fine in the
          // mobile composer this grid was modelled on and was absurd here — the console
          // card is ~1800px wide, so one photo rendered ~1350px tall and pushed every
          // other post off the screen. A moderator is scanning posts, not viewing an
          // album; the thumbnail's job is to say "there is a photo, and roughly what of".
          // Full size is one click away.
          <div className={`mt-3 grid max-w-2xl gap-1.5 ${
            media.length === 1 ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3'}`}>
            {media.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setLightbox(m)}
                className="group relative overflow-hidden rounded-md border border-gray-200 bg-gray-50"
                aria-label="View photo full size"
              >
                <AuthedImage
                  // The THUMBNAIL here and the full blob in the lightbox: a page of
                  // posts should not pull full-resolution photos it will only show at
                  // 200px. Falls back to the full path when no thumbnail was stored.
                  path={m.thumbnailPath || m.path}
                  alt="Post photo"
                  className="h-44 w-full object-cover transition group-hover:opacity-90"
                />
              </button>
            ))}
          </div>
        )}
        {removed && post.deletedReason && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
            Removed: {post.deletedReason}
          </p>
        )}
        {unpublished && post.reviewNote && (
          <p className={`mt-3 rounded-lg px-3 py-2 text-xs ${
            reviewState === 'held' ? 'bg-amber-50 text-amber-800' : 'bg-rose-50 text-rose-700'}`}>
            {reviewState === 'held' ? post.reviewNote : `Not approved: ${post.reviewNote}`}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1 border-t border-gray-100 bg-gray-50 px-2.5 py-1.5">
        <div className="inline-flex items-center">
          <button
            onClick={toggleLike}
            disabled={busy || removed || unpublished}
            aria-pressed={liked}
            aria-label="Like"
            title={liked ? 'Unlike' : 'Like'}
            className={`inline-flex items-center rounded-md py-1.5 pl-2.5 pr-1 text-sm font-medium disabled:opacity-50 ${
              liked ? 'text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'}`}
          >
            <Heart size={15} fill={liked ? 'currentColor' : 'none'} />
          </button>
          {countButton('likes', likes, 'See who liked this',
            liked ? 'text-blue-700' : 'text-gray-500 hover:text-gray-800')}
        </div>
        <div className="inline-flex items-center">
          <button
            onClick={openComments}
            disabled={unpublished}
            aria-label={showComments ? 'Hide comments' : 'Show comments'}
            title={showComments ? 'Hide comments' : 'Show comments'}
            className="inline-flex items-center rounded-md py-1.5 pl-2.5 pr-1 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50"
          >
            <MessageCircle size={15} />
          </button>
          {countButton('comments', commentCount, 'See who commented', 'text-gray-500 hover:text-gray-800')}
        </div>
        {onEngagement ? (
          <button
            type="button"
            onClick={() => onEngagement(post, 'viewers')}
            title="See who has seen this"
            aria-label={`${fmtCount(post.viewCount)} views — see who has seen this`}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium tabular-nums text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          >
            <Eye size={15} /> {fmtCount(post.viewCount)}
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium tabular-nums text-gray-500">
            <Eye size={15} /> {fmtCount(post.viewCount)}
          </span>
        )}
        <span className="flex-1" />
        <button
          onClick={() => onReview(post)}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
        >
          {removed ? <><RotateCcw size={14} /> Restore</> : <><ShieldAlert size={14} /> Review</>}
        </button>
        {/* Not on a held / not-approved post: it is on nobody's feed to take down, and
            a held one is approved or rejected from AI review instead. */}
        {!removed && !unpublished && (
          <button
            onClick={() => onReview(post)}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            <Trash2 size={14} /> Take down
          </button>
        )}
      </div>

      {notice && (
        <div className="border-t border-gray-100 bg-amber-50 px-4 py-2 text-xs text-amber-800">{notice}</div>
      )}

      {/* Full-size viewer. Rendered inside the card rather than portalled: it is
          transient and owns no page state, and the review drawer beside it is the
          thing that genuinely needed a portal to escape the scaffold's overflow. */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Photo full size"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            aria-label="Close"
          >
            <X size={20} />
          </button>
          {/* Stop propagation so clicking the photo itself does not dismiss — only the
              backdrop and the X do. */}
          <div onClick={(e) => e.stopPropagation()} className="max-h-full max-w-full">
            <AuthedImage
              path={lightbox.path}
              alt="Post photo, full size"
              className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain"
            />
          </div>
        </div>
      )}

      {showComments && (
        <div className="border-t border-gray-100 px-4 py-3">
          {comments === null ? (
            <InlineLoader label="Loading comments…" className="flex py-2" />
          ) : comments.length === 0 ? (
            <p className="py-1 text-sm text-gray-400">No comments yet.</p>
          ) : (
            <div className="space-y-3">
              {comments.map((c) => (
                <div key={c.id} className="flex gap-2.5">
                  <div className="grid h-7 w-7 flex-none place-items-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600">
                    {initials(c.author?.name || '?')}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`rounded-lg border px-3 py-2 ${
                      c.reviewState === 'held' ? 'border-amber-200 bg-amber-50/60' : 'border-gray-100 bg-gray-50'}`}>
                      <div className="text-xs font-semibold text-gray-800">
                        {c.author?.name}
                        {c.reviewState === 'held' && (
                          <span className="ml-1.5 font-medium text-amber-700">· Under review</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-700">{c.content}</div>
                    </div>
                    <div className="px-1 pt-0.5 text-[11px] text-gray-400">{timeAgo(c.createdAt)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!removed && !unpublished && (
            <div className="mt-3 flex gap-2">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitComment(); }}
                placeholder="Write a comment…"
                className="flex-1 rounded-full border border-gray-200 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
              <button
                onClick={submitComment}
                disabled={!draft.trim() || busy}
                className="rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Post
              </button>
            </div>
          )}
        </div>
      )}
    </article>
  );
}
